import type { ModelEntitySearchResponse } from '@upright/common/model/search/api-types';
import {
  allSearchTypes,
  ModelEntitySearchResponseItem,
  SearchEntityType,
  searchTypeHasSemanticSupport,
} from '@upright/common/model/search/types';
import { useEntitySearchQueries } from '@upright/ui/api/model/search/hooks';
import useDebounce from '@upright/ui/common/generic/hooks/external/use-debounce';
import { uniqWith } from 'lodash';
import { useMemo } from 'react';

// Threshold to return this number of ES hits before the semantic search hits.
const NO_OF_TOP_ES_MATCH = 3;

// Debounce timer used for search.
const SEARCH_DEBOUNCE_TIMER = 200;

/**
 * Hook that handles logic of the main search. It forms the query object which fires automatically.
 */
export function useEntitySearch(
  inputValue: string,
  entityTypesToFetch: readonly SearchEntityType[] | 'all',
  options?: {
    semanticSearchMode?: 'none' | 'combine both' | 'only semantic';
    skipShortQueryFilter?: boolean;
    onlyPublicEntities?: boolean;
    includeVisibility?: boolean;
    allResultsEnabled?: boolean; // TODO ML: This really should be a prop of the option component, move there
    maxResultsToShow?: number;
    showProductCode?: boolean;
  },
) {
  const semanticSearchMode = options?.semanticSearchMode ?? 'none';
  const entityTypesOrAll = entityTypesToFetch === 'all' ? allSearchTypes : entityTypesToFetch;
  assertSemanticSearchModeIsCompatibleWithEntityTypes(entityTypesOrAll, semanticSearchMode);
  const preprocessedSearchInput = preprocessSearchInput(inputValue);

  const debouncedSearchInput = useDebounce(preprocessedSearchInput, SEARCH_DEBOUNCE_TIMER);
  const isDebouncing = preprocessedSearchInput !== debouncedSearchInput;

  const { basicSearchQuery: basicQuery, semanticSearchQuery: semanticQuery } = useEntitySearchQueries(
    debouncedSearchInput,
    entityTypesOrAll,
    semanticSearchMode,
    {
      enabled: isSearchTriggeredOnInput(debouncedSearchInput),
      onlyPublicEntities: options?.onlyPublicEntities,
      includeVisibility: options?.includeVisibility,
    },
  );

  const searchOptions = useMemo(() => {
    if (basicQuery.data || semanticQuery.data) {
      return transformResponsesToOptions(basicQuery.data ?? [], semanticQuery.data ?? [], preprocessedSearchInput, {
        allResultsEnabled: options?.allResultsEnabled,
        originalInputValue: inputValue,
        maxResultsToShow: options?.maxResultsToShow,
        skipShortQueryFilter: options?.skipShortQueryFilter,
      });
    } else {
      return [];
    }
  }, [
    basicQuery.data,
    semanticQuery.data,
    preprocessedSearchInput,
    options?.allResultsEnabled,
    inputValue,
    options?.maxResultsToShow,
    options?.skipShortQueryFilter,
  ]);

  return {
    basicQuery,
    semanticQuery,
    preprocessedSearchInput,
    searchOptions,
    isDebouncing,
    errorMessage: basicQuery.error?.message || semanticQuery.error?.message || null,
  };
}

function transformResponsesToOptions(
  basicResponse: ModelEntitySearchResponse,
  semanticResponse: ModelEntitySearchResponse,
  preprocessedSearchInput: string,
  options?: {
    allResultsEnabled?: boolean;
    skipShortQueryFilter?: boolean;
    originalInputValue?: string;
    maxResultsToShow?: number;
  },
): ModelEntitySearchResponseItem[] {
  const basicOptionsAllFields = addQueryAndIsDisabledFields(
    basicResponse,
    preprocessedSearchInput,
    options?.allResultsEnabled,
  );
  const semanticOptionsAllFields = addQueryAndIsDisabledFields(
    semanticResponse,
    preprocessedSearchInput,
    options?.allResultsEnabled,
  );

  // TODO ML: This filtering should be done using the select component filterOptions prop. Move it there!
  /*
   * At exactly 2 characters the basic (Elasticsearch) hits are filtered down to near-exact name
   * matches. It is a harsh filter — it discards every hit the backend scored, so a search that found
   * plenty can end up looking like one that found nothing — and a caller whose result list can carry
   * loose matches should pass `skipShortQueryFilter` to switch it off.
   */
  const basicOptionsWithStricterMatchIfQueryIsShort =
    preprocessedSearchInput.length === 2 && !options?.skipShortQueryFilter
      ? filterOnShortQuery(basicOptionsAllFields, preprocessedSearchInput)
      : basicOptionsAllFields;

  const optionsCombined = combineOptionLists(
    basicOptionsWithStricterMatchIfQueryIsShort,
    semanticOptionsAllFields,
    options?.allResultsEnabled,
  );

  // Remove duplicate results in the order that they appear
  const optionsCombinedWithoutDuplicates = uniqWith(
    optionsCombined,
    (resultA, resultB) => resultA.type === resultB.type && resultA.id === resultB.id,
  );

  return optionsCombinedWithoutDuplicates.slice(0, options?.maxResultsToShow);
}

function combineOptionLists(
  basicOptions: ModelEntitySearchResponseItem[],
  semanticOptions: ModelEntitySearchResponseItem[],
  allResultsEnabled: boolean = false,
): ModelEntitySearchResponseItem[] {
  const [excellentBasicOptions, poorBasicOptions] = [
    basicOptions.slice(0, NO_OF_TOP_ES_MATCH),
    basicOptions.slice(NO_OF_TOP_ES_MATCH + 1),
  ];

  const combinedOptionList = [...excellentBasicOptions, ...semanticOptions, ...poorBasicOptions];

  // Ensure that the first, default result is not a teaser if available.
  const firstNonTeaser = allResultsEnabled ? undefined : combinedOptionList.find(opt => !opt.teaser);
  if (firstNonTeaser) {
    const optionsExceptTheTeaser = combinedOptionList.filter(opt => opt !== firstNonTeaser);
    return [firstNonTeaser].concat(optionsExceptTheTeaser);
  } else {
    return combinedOptionList;
  }
}

function addQueryAndIsDisabledFields(
  items: ModelEntitySearchResponseItem[],
  preprocessedInput: string,
  allResultsEnabled: boolean = false,
) {
  return items.map(item => ({
    ...item,
    query: preprocessedInput, // TODO: No need to return this from the backend, remove from there
    isDisabled: allResultsEnabled ? false : !!item.teaser,
  }));
}

/**
 * Used to decide if to show searchResult option on short (length = 2) query.
 */
function filterOnShortQuery(items: ModelEntitySearchResponseItem[], inputWithoutShortWords: string) {
  return items.filter(item => {
    // Perfect match
    if (item.name.toLowerCase() === inputWithoutShortWords.toLowerCase()) {
      return true;
    }
    // Or the entity starts with a word that matches
    if (item.name.split(' ')[0].toLowerCase() === inputWithoutShortWords.toLowerCase()) {
      return true;
    }
    // Otherwise no
    return false;
  });
}

function preprocessSearchInput(inputValue: string): string {
  const inputWithoutShortWords = inputValue
    .split(/\s+/) // \s = whitespace
    .join(' ')
    .trim();
  return inputWithoutShortWords;
}

export function isSearchTriggeredOnInput(preprocessedInput: string) {
  return preprocessedInput.length > 1;
}

function assertSemanticSearchModeIsCompatibleWithEntityTypes(
  searchTypes: readonly SearchEntityType[],
  semanticSearchMode: 'none' | 'combine both' | 'only semantic',
) {
  if (semanticSearchMode === 'none') return;
  if (searchTypes.every(searchType => searchTypeHasSemanticSupport(searchType))) return;
  throw Error(
    `Given semantic search mode "${semanticSearchMode}" is not compatible with the chosen search entity types.`,
  );
}
